import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useRef, useEffect } from "react";
import { Helmet } from "react-helmet";

interface ImagePair {
    left_id: number;
    right_id: number;
    leftObjects: string;
    rightNumber: number;
}

// Function to generate a random number within a range
const getRandomNumber = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;



const MatchObjectWithNumber: React.FC = () => {
    const [connections, setConnections] = useState<{ x1: number; y1: number; x2: number; y2: number; correct: boolean }[]>([]);
    const [selectedLeft, setSelectedLeft] = useState<{ id: number; x: number; y: number } | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);
    const [questions, setQuestions] = useState<ImagePair[]>([]);
    const [height, setHeight] = useState('0px');
    const [showImage, setShowImage] = useState(false);

    const modal = useRef<HTMLIonModalElement>(null);

    // Get position relative to container
    const getRelativePosition = (element: HTMLDivElement) => {
        const container = containerRef.current;
        if (!container) return { x: 0, y: 0 };

        const elementRect = element.getBoundingClientRect();
        setHeight(container.scrollHeight + 'px');

        const screenWidth = window.innerWidth;
        let elementX, elementY;
        if (screenWidth >= 1024) {
            elementX = element.offsetLeft + elementRect.width;
            elementY = element.offsetTop + elementRect.height / 2;
        }
        else {
            elementX = element.offsetLeft + elementRect.width / 2;
            elementY = element.offsetTop + elementRect.height / 2;
        }

        return {
            x: elementX,
            y: elementY
        }
    };

    // Handle left click
    const handleLeftClick = (event: React.MouseEvent<HTMLDivElement>, id: number) => {
        const position = getRelativePosition(event.currentTarget);
        setSelectedLeft({ id, x: position.x, y: position.y });
    };

    // Handle right click and check if the match is correct
    const handleRightClick = (event: React.MouseEvent<HTMLDivElement>, rightId: number) => {
        if (selectedLeft) {
            const position = getRelativePosition(event.currentTarget);
            const isCorrect = selectedLeft.id === rightId;

            setConnections([...connections, { x1: selectedLeft.x, y1: selectedLeft.y, x2: position.x, y2: position.y, correct: isCorrect }]);
            setSelectedLeft(null);
        }
    };

    // Reset connections
    const resetConnections = () => {
        setConnections([]);
        setSelectedLeft(null);
    };

    // Shuffle function for randomizing right column numbers
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            // Generate a random index
            const randomIndex = Math.floor(Math.random() * (i + 1));
            // Swap elements
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    // Generate new set of questions
    const fetchQuestions = () => {
        const emojiSet = ["🍎", "🍌", "🍇", "🍓", "🍉", "🥕", "🌸", "🐶", "🐱"];
        const shuffledEmojis = shuffleArray([...emojiSet]);
        const newQuestions: ImagePair[] = [];
        const usedNumbers: number[] = [];

        for (let i = 1; i <= 4; i++) { // Generate 4 pairs
            let objectCount;
            do {
                objectCount = getRandomNumber(1, 9);
            } while (usedNumbers.includes(objectCount)); // Ensure unique numbers
            usedNumbers.push(objectCount);
        }
        const suffledNumbers = shuffleArray([...usedNumbers]);
        usedNumbers.map((number, index) => {
            newQuestions.push({
                left_id: index + 1,
                right_id: usedNumbers.indexOf(suffledNumbers[index]) + 1,
                leftObjects: shuffledEmojis[index].repeat(number),
                rightNumber: suffledNumbers[index],
            });
        })

        console.log(newQuestions)

        setQuestions(newQuestions);
    };

    useEffect(() => {
        resetConnections();
        fetchQuestions();
        document.title = "Match Object with Number – Nursery Maths Worksheet | Mama’s Guidance"; // Set page title
    }, []);

    const nextQuestion = () => {
        resetConnections();
        setShowImage(true);
        setTimeout(() => {
            fetchQuestions();
            setShowImage(false);
        }, 2000);
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Teach nursery kids number recognition with the "Match Object with Number" activity on Mamas Guidance—an engaging way for children to count objects and match them with the correct number, building early math skills through fun practice.' />
            </Helmet>
            {!showImage ? (
                <>
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>Match Object with Number</h1>

                            <p>
                                Looking for a fun way to teach numbers to your toddler? This <strong>Match Object with Number</strong> worksheet helps nursery and preschool kids develop early math skills by <strong>connecting pictures to numbers</strong>. With colorful visuals and easy-to-follow instructions, learning numbers becomes playful!
                            </p>

                            <h2>🔍 What Does This Worksheet Do?</h2>
                            <p>
                                In this matching activity, children see groups of objects (like balloons, animals, or stars) on one side and numbers on the other. Their task is to <strong>draw lines to match the correct number of objects with the corresponding number</strong>.
                            </p>

                            <h2>🎯 What Will Your Child Learn?</h2>
                            <ul>
                                <li>Count objects accurately</li>
                                <li>Recognize numbers from 1 to 20</li>
                                <li>Match quantities with numerals</li>
                                <li>Improve hand-eye coordination and focus</li>
                            </ul>

                            <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                            <ul>
                                <li>First, count the objects aloud with your child.</li>
                                <li>Use a pencil or crayon to draw lines connecting the pictures to the right numbers.</li>
                                <li>Repeat the activity regularly to build confidence and speed.</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/nursery/mathematics/count-object"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Count Object</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/missing-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Missing Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/nursery/mathematics/bigger-or-smaller"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Bigger Or Smaller</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🧠 Why Matching Activities Work</h2>
                            <p>
                                <strong>Matching games</strong> connect visual learning with number sense. Children begin to understand that numbers represent quantities — a critical early math <strong>milestone</strong>. This worksheet builds that understanding in a <strong>joyful way</strong>.
                            </p>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>Match Object with Number – Nursery Maths Worksheet</h1>

                        <p>
                            Looking for a fun way to teach numbers to your toddler? This <strong>Match Object with Number</strong> worksheet helps nursery and preschool kids develop early math skills by <strong>connecting pictures to numbers</strong>. With colorful visuals and easy-to-follow instructions, learning numbers becomes playful!
                        </p>

                        <h2>🔍 What Does This Worksheet Do?</h2>
                        <p>
                            In this matching activity, children see groups of objects (like balloons, animals, or stars) on one side and numbers on the other. Their task is to <strong>draw lines to match the correct number of objects with the corresponding number</strong>.
                        </p>

                        <h2>🎯 What Will Your Child Learn?</h2>
                        <ul>
                            <li>Count objects accurately</li>
                            <li>Recognize numbers from 1 to 20</li>
                            <li>Match quantities with numerals</li>
                            <li>Improve hand-eye coordination and focus</li>
                        </ul>


                        <h2>👩‍🏫 Tips for Parents & Teachers</h2>
                        <ul>
                            <li>First, count the objects aloud with your child.</li>
                            <li>Use a pencil or crayon to draw lines connecting the pictures to the right numbers.</li>
                            <li>Repeat the activity regularly to build confidence and speed.</li>
                        </ul>


                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/nursery/mathematics/count-object"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Count Object</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/mathematics/missing-number"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Missing Number</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/nursery/mathematics/bigger-or-smaller"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Bigger Or Smaller</IonLabel>
                            </IonItem>
                        </div>



                        <h2>🧠 Why Matching Activities Work</h2>
                        <p>
                            <strong>Matching games</strong> connect visual learning with number sense. Children begin to understand that numbers represent quantities — a critical early math <strong>milestone</strong>. This worksheet builds that understanding in a <strong>joyful way</strong>.
                        </p>

                    </article>
                    <div ref={containerRef} className="contentPart additionWord matchObject  match-container letterOption objectLetter" style={{ position: "relative", marginTop: "0%", paddingTop: "0%", height: "calc(-200px + 100vh)" }}>
                        {/* SVG for drawing connections */}
                        <svg ref={svgRef} className="connector-svg" height={height} style={{ position: "absolute", top: 0, left: 0, width: "100%", pointerEvents: "none", zIndex: 10 }}>
                            {connections.map((conn, index) => (
                                <line
                                    key={index}
                                    x1={conn.x1}
                                    y1={conn.y1}
                                    x2={conn.x2}
                                    y2={conn.y2}
                                    stroke={conn.correct ? "green" : "red"} // Green if correct, Red if incorrect
                                    strokeWidth="3"
                                />
                            ))}
                        </svg>

                        {/* <h6>Match the Object Count to the Number</h6> */}

                        <div className="mb-2 columns" style={{ display: "flex", justifyContent: "space-between", position: "relative", zIndex: 5, width: "100%" }}>
                            {/* Left Column - Objects */}
                            <div className="left-column">
                                {questions.map((item) => (
                                    <div
                                        key={item.left_id}
                                        className={`image-box object-number ${selectedLeft?.id === item.left_id ? "selected" : ""}`}
                                        onClick={(e) => handleLeftClick(e, item.left_id)}
                                        style={{ cursor: "pointer", padding: "10px", textAlign: "center" }}
                                    >
                                        {item.leftObjects}
                                    </div>
                                ))}
                            </div>

                            {/* Right Column - Numbers */}
                            <div className="right-column">
                                {questions.map((item) => (
                                    <div
                                        key={item.right_id}
                                        className="image-box"
                                        onClick={(e) => handleRightClick(e, item.right_id)}
                                        style={{ cursor: "pointer", padding: "10px", textAlign: "center" }}
                                    >
                                        {item.rightNumber}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Controls */}
                        <div className="mt-2 mb-4 text-center" style={{ display: "flex", gap: "10px" }}>
                            <div className="optionButton background-blue" style={{ fontSize: "18px" }} onClick={resetConnections}>
                                Reset
                            </div>
                            <div className="optionButton2 background-blue" style={{ fontSize: "18px" }} onClick={() => nextQuestion()}>
                                Next &gt;&gt;
                            </div>
                        </div>
                    </div>
                </>
            ) : (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_LOADING_LINK} />
                </div>
            )}
        </>
    );
};

export default MatchObjectWithNumber;
